import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-vault flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-14 w-14 rounded-xl bg-[#9333EA] flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-black text-white tracking-tight mb-2">
            {sent ? 'Check Your Email' : 'Forgot Password?'}
          </h1>
          <p className="text-[#A19DB0]">
            {sent ? 'We sent a reset link if that email is registered.' : 'No worries. Enter your email and we\'ll send you a reset link.'}
          </p>
        </div>

        {sent ? (
          <div className="space-y-6">
            <div className="p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
              <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-white font-semibold">Reset link sent</p>
              <p className="text-sm text-[#A19DB0] mt-1">Check your inbox for an email from FutureVault. Click the link to reset your password.</p>
              <p className="text-xs text-[#A19DB0] mt-3">Don't see it? Check your spam folder.</p>
            </div>
            <Link to="/login">
              <Button variant="outline" className="w-full h-12 btn-secondary font-semibold">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0]">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A19DB0]" />
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 input-dark h-12"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="forgot-submit-button"
              disabled={loading}
              className="w-full h-12 btn-primary font-semibold"
            >
              {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Sending...</> : 'Send Reset Link'}
            </Button>
            <p className="text-center text-xs text-[#A19DB0]">
              Remember your password? <Link to="/login" className="text-[#9333EA] hover:text-[#A855F7] font-semibold">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
